<template>
    <div class="onboarding-choice">
        <CompassTooltip
            :content="$t.__('pdp.mandate.OnboardingChoice.tooltip')"
            :disabled="!disabled"
            :arrow="false"
        >
            <button :disabled="disabled" :class="classes" @click="$emit('select', type)">
                <img :src="`/ressources/logged/img/illustrations/pdp/${icon}.svg`" :alt="label" />
                <span class="onboarding-choice__button-label">
                    {{ label }}
                </span>
                <CompassIcon v-if="state === BUTTON_STATES.SELECTED" icon="success-circle-fill" />
            </button>
        </CompassTooltip>
        <div v-if="showTags" class="onboarding-choice__tags">
            <CompassTag color="stroked" size="small">
                {{ $t.__('pdp.mandate.OnboardingChoice.issue.tag') }}
            </CompassTag>
            <CompassTag v-if="type === 'receipt'" color="stroked" size="small">
                {{ $t.__('pdp.mandate.OnboardingChoice.receipt.tag') }}
            </CompassTag>
        </div>
        <div class="onboarding-choice__checklist">
            <div
                v-for="(item, index) in checklist"
                :key="index"
                class="onboarding-choice__item typographies-paragraph-medium-regular"
            >
                <CompassIcon icon="success-circle" size="small" />
                {{ item }}
            </div>
        </div>
    </div>
</template>
<script>
const BUTTON_STATES = Object.freeze({
    DEFAULT: 'default',
    SELECTED: 'selected',
    UNSELECTED: 'unselected',
});
export default {
    props: {
        type: {
            type: String,
            required: true,
            validate: (value) => {
                return ['issue', 'receipt', 'sellsy', 'other'].includes(value);
            },
        },
        state: {
            type: String,
            default: BUTTON_STATES.DEFAULT,
            validate: (value) => {
                return Object.values(BUTTON_STATES).includes(value);
            },
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            BUTTON_STATES,
            checklist: [
                this.$t.__(`pdp.mandate.OnboardingChoice.${this.type}.checklist_1`),
                this.$t.__(`pdp.mandate.OnboardingChoice.${this.type}.checklist_2`),
                ['issue', 'receipt'].includes(this.type)
                    ? this.$t.__(`pdp.mandate.OnboardingChoice.${this.type}.checklist_3`)
                    : null,
            ],
        };
    },
    computed: {
        classes() {
            return {
                'onboarding-choice__button': true,
                'typographies-paragraph-large-bold': true,
                'onboarding-choice__button--is-selected': this.state === BUTTON_STATES.SELECTED,
                'onboarding-choice__button--is-unselected': this.state === BUTTON_STATES.UNSELECTED,
            };
        },
        showTags() {
            return ['issue', 'receipt'].includes(this.type);
        },
        icon() {
            switch (this.type) {
                case 'sellsy':
                    return 'only-sellsy';
                case 'other':
                    return 'multi-tool';
                case 'issue':
                    return 'pdp-issue';
                case 'receipt':
                    return 'pdp-receipt';
                default:
                    return '';
            }
        },
        label() {
            return this.$t.__(`pdp.mandate.OnboardingChoice.${this.type}.label`);
        },
    },
    created() {
        this.checklist = this.checklist.filter((item) => item);
    },
};
</script>
<style lang="scss" scoped>
.onboarding-choice {
    flex: 1;

    &__button {
        align-items: center;
        background: var(--colors-common-background-default);
        border: 0;
        border-radius: var(--spacing-0);
        box-shadow: var(--elevation-1);
        color: var(--colors-common-text-standard-default);
        cursor: pointer;
        display: flex;
        gap: var(--spacing-3);
        min-height: 104px;
        padding: var(--spacing-3);
        position: relative;
        text-align: left;
        transition: all 200ms ease-in-out;
        width: 100%;

        &:not(:disabled) {
            &:hover {
                box-shadow: var(--box-shadows-large);
            }
        }

        &:disabled {
            cursor: not-allowed;
        }

        img {
            flex-shrink: 0;
        }

        &:deep(.compass-icon) {
            color: var(--colors-common-text-success-default);
            position: absolute;
            right: 0;
            top: 0;
            transform: translate(4px, -4px);
        }

        &--is-selected {
            outline: 1px solid var(--colors-common-border-darkest);

            &:hover {
                box-shadow: var(--elevation-1);
            }
        }

        &--is-unselected {
            box-shadow: none;
            color: var(--colors-common-text-standard-disabled);
        }
    }

    &__tags {
        display: flex;
        gap: var(--spacing-1);
        margin: var(--spacing-2) 0 0;
    }

    &__checklist {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-2);
        margin: var(--spacing-2) 0 0;
    }

    &__item {
        color: var(--colors-common-text-subtle-default);
        display: flex;
        gap: var(--spacing-0);

        &:deep(.compass-icon) {
            flex-shrink: 0;
            margin-top: 2px;
        }
    }
}
</style>
